import type { MatchPlayer, MatchRecord, Stats } from './types.ts';

/**
 * Trophies (GDD §15): given once, for good. Each is judged on the rap sheet after a match and on the
 * match itself. Their names and descriptions are in the translation files, by id.
 */

export interface TrophyDef {
  id: string;
  /** True when the rap sheet or this match earns it. */
  earned: (after: Stats, match: MatchRecord, player: MatchPlayer) => boolean;
}

const atLeast =
  (key: keyof Stats, n: number): TrophyDef['earned'] =>
  (after) =>
    after[key] >= n;

export const TROPHIES: readonly TrophyDef[] = [
  { id: 'first_heist', earned: atLeast('heists', 1) },
  { id: 'ten_heists', earned: atLeast('heists', 10) },
  { id: 'fifty_heists', earned: atLeast('heists', 50) },
  { id: 'millionaire', earned: atLeast('loot', 1000) },
  { id: 'fat_share', earned: atLeast('bestLoot', 150) },
  { id: 'mole_hunter', earned: atLeast('unmasksRight', 5) },
  { id: 'bloodhound', earned: atLeast('unmasksRight', 25) },
  { id: 'double_agent', earned: atLeast('infiltratorWins', 3) },
  { id: 'daily_regular', earned: atLeast('dailyRuns', 7) },
  { id: 'veteran', earned: atLeast('matches', 100) },
  // On the match: the alarm never rose, and the heist was brought home.
  {
    id: 'clean_getaway',
    earned: (_after, match, player) => match.success && match.alarmFinal === 0 && !player.abandoned,
  },
  // On the match: a legend-level heist, won.
  {
    id: 'legend_job',
    earned: (_after, match, player) => match.success && match.difficulty === 'legend' && player.won,
  },
];

export const TROPHY_IDS: readonly string[] = TROPHIES.map((t) => t.id);

/** The trophies that this match earns a player who does not have them yet. */
export function trophiesEarned(
  after: Stats,
  match: MatchRecord,
  player: MatchPlayer,
  have: ReadonlySet<string>,
): string[] {
  if (player.abandoned) return [];
  return TROPHIES.filter((t) => !have.has(t.id) && t.earned(after, match, player)).map((t) => t.id);
}
